import React, { useState } from 'react';
import { Button, Box, Typography } from '@mui/material';
import { useHistory, useLocation } from 'react-router-dom';
import MfaLayout from '../../components/MFA/MfaLayout';
import DigitInputGroup from '../../components/MFA/DigitInput';
import { confirmMfaEnrollment, verifyMfaCode } from '../../services/mfaService';
import { useAuth } from '../../context/AuthContext';

const MfaVerification = () => {
  const history = useHistory();
  const location = useLocation();
  const { loginSuccess } = useAuth();

  const [digitValues, setDigitValues] = useState(Array(6).fill(''));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const { mfaToken, mode } = location.state || {};
  const isEnrollment = mode === 'enrollment';
  const isCodeComplete = digitValues.every((digit) => digit !== '');

  const getSubtitle = () => {
    if (error) return 'Code incorrect. Vérifiez votre application et réessayez.';
    if (isEnrollment) return "Saisissez le code de confirmation de votre application d'authentification";
    return "Saisissez le code à 6 chiffres de votre application d'authentification";
  };

  React.useEffect(() => {
    if (!mfaToken) history.push('/login');
  }, [mfaToken, history]);

  React.useEffect(() => {
    const timer = setTimeout(() => {
      const firstInput = document.querySelector('input[data-index="0"]');
      if (firstInput) firstInput.focus();
    }, 200);
    return () => clearTimeout(timer);
  }, []);

  const verifyCode = async (code) => {
    try {
      setLoading(true);
      setError(false);

      let response;
      if (isEnrollment) {
        response = await confirmMfaEnrollment(mfaToken, code);
      } else {
        response = await verifyMfaCode(mfaToken, code);
      }

      if (response.success && response.token) {
        loginSuccess(response.user, response.token);
        history.push('/');
      } else {
        throw new Error('Réponse inattendue du serveur');
      }
    } catch (err) {
      if (err.response?.status === 401) {
        window.location.href = '/login?reason=mfa_expired';
        return;
      }
      setError(true);
      setDigitValues(Array(6).fill(''));
      setTimeout(() => {
        const firstInput = document.querySelector('input[data-index="0"]');
        if (firstInput) firstInput.focus();
      }, 100);
    } finally {
      setLoading(false);
    }
  };

  const handleDigitChange = (index, value) => {
    const newValues = [...digitValues];
    newValues[index] = value;
    setDigitValues(newValues);
    if (error) setError(false);
  };

  const handlePasteAll = (digits) => {
    setDigitValues(digits);
    if (error) setError(false);
    verifyCode(digits.join(''));
  };

  const handleComplete = async () => {
    const code = digitValues.join('');
    if (code.length !== 6) return;
    await verifyCode(code);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const code = digitValues.join('');
    if (code.length !== 6) return;
    await verifyCode(code);
  };

  return (
    <MfaLayout title="CONNEXION" subtitle={getSubtitle()} hasError={error}>
      <form onSubmit={handleSubmit} style={{ width: '100%' }}>
        <Box marginTop="30px" width="100%">
          <DigitInputGroup
            values={digitValues}
            onChange={handleDigitChange}
            onPasteAll={handlePasteAll}
            hasError={error}
            onComplete={handleComplete}
            disabled={loading}
          />

          <Box textAlign="center" marginTop="10px" marginBottom="20px">
            <Typography
              variant="caption"
              sx={{ color: '#b71c1c', fontSize: '12px', fontWeight: 500 }}
            >
              En cas de perte d&apos;accès à votre application d&apos;authentification,
              veuillez nous contacter.
            </Typography>
          </Box>

          <div
            style={{
              width: '100%',
              borderTop: '2px solid #CCCCCC',
              margin: '20px 0px',
              marginBottom: 35,
            }}
          />

          <Button
            disabled={!isCodeComplete || loading}
            variant="contained"
            color="primary"
            fullWidth
            size="large"
            type="submit"
            sx={{
              '&.Mui-disabled': {
                backgroundColor: '#1976d2',
                opacity: 0.4,
                color: '#fff',
              },
            }}
          >
            {loading ? 'VÉRIFICATION...' : 'CONTINUER'}
          </Button>
        </Box>
      </form>
    </MfaLayout>
  );
};

export default MfaVerification;
